/**
 * Skew-T Log-P da radiossonda.
 *
 * Segue o desenho dos scripts do MetPy (Unidata): pressão em escala
 * logarítmica, isotermas inclinadas a 45°, adiabáticas secas e úmidas, linhas
 * de razão de mistura, temperatura em vermelho, ponto de orvalho em verde, a
 * parcela levantada em tracejado e as áreas de energia pintadas — positiva
 * (CAPE) em vermelho, negativa (CIN) em azul —, com as barbelas do vento à
 * direita.
 *
 * A termodinâmica está em `termo.ts`; aqui é só geometria e desenho.
 */
import { useMemo, useState } from 'react';
import { kmNoNivel } from './km';
import { camadasDeNuvem, rotuloDaCamada } from './nuvens';
import { nivelMaisProximo } from './perfil';
import { adiabaticaSeca, adiabaticaUmida, areasDeEnergia, barbela, orvalhoDaMistura, parcelaDoPerfil, type Parcela, type PontoDeArea } from './termo';
import type { Nivel } from './types';

// ---------------------------------------------------------------------------
// Geometria (pura, testada em `skewt.test.ts`)
// ---------------------------------------------------------------------------

/** Faixa de pressão do diagrama (hPa). A base passa de 1000: a superfície da Amazônia fica em ~1010. */
export const P_BASE = 1050;
export const P_TOPO = 100;
/** Faixa de temperatura NA BASE do diagrama (°C). */
export const T_ESQ = -35;
export const T_DIR = 45;

/** Área de desenho e margens, no `viewBox`. */
export const W = 140;
export const H = 150;
const PAD_ESQ = 17;
const PAD_CIMA = 6;
const PAD_BAIXO = 10;
/** Coluna das barbelas e dos rótulos de altura, à direita. */
const COL_VENTO = 22;
const COL_KM = 15;
export const VB_W = PAD_ESQ + W + COL_VENTO + COL_KM;
export const VB_H = PAD_CIMA + H + PAD_BAIXO;

/** Inclinação: com `H / W` as isotermas ficam a 45° na tela. */
const INCLINACAO = H / W;

/** Posição vertical de uma pressão, de 0 (topo) a 1 (base), em ln(p). */
export function fracaoDaPressao(p: number): number {
  return (Math.log(p) - Math.log(P_TOPO)) / (Math.log(P_BASE) - Math.log(P_TOPO));
}

export function pressaoDaFracao(k: number): number {
  return Math.exp(Math.log(P_TOPO) + Math.min(1, Math.max(0, k)) * (Math.log(P_BASE) - Math.log(P_TOPO)));
}

/** Ponto (x, y) no `viewBox` de uma temperatura (°C) numa pressão (hPa). */
export function ponto(t: number, p: number): { x: number; y: number } {
  const k = fracaoDaPressao(p);
  return {
    x: PAD_ESQ + ((t - T_ESQ) / (T_DIR - T_ESQ) + INCLINACAO * (1 - k)) * W,
    y: PAD_CIMA + k * H,
  };
}

function caminho(pts: readonly { x: number; y: number }[]): string {
  return pts.map((q, i) => `${i ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join('');
}

const ISOBARAS = [1000, 850, 700, 500, 400, 300, 200, 150, 100];
/** Pressões em que as curvas de fundo são amostradas. */
const GRADE = Array.from({ length: 48 }, (_, i) => pressaoDaFracao(1 - i / 47));

interface Fundo {
  isotermas: { t: number; d: string }[];
  secas: string[];
  umidas: string[];
  mistura: { w: number; d: string; rotulo: { x: number; y: number } }[];
}

let fundoPronto: Fundo | null = null;

/** As cinco famílias de linhas de fundo: iguais para toda sondagem, calculadas uma vez. */
export function fundo(): Fundo {
  if (fundoPronto) return fundoPronto;
  const isotermas = [];
  for (let t = -120; t <= 50; t += 10) isotermas.push({ t, d: caminho([ponto(t, P_BASE), ponto(t, P_TOPO)]) });
  const secas = [];
  for (let theta = -30; theta <= 190; theta += 20) secas.push(caminho(GRADE.map((p) => ponto(adiabaticaSeca(theta, 1000, p), p))));
  const umidas = [];
  for (const t0 of [-10, 0, 8, 14, 20, 24, 28, 32]) umidas.push(caminho(GRADE.map((p) => ponto(adiabaticaUmida(t0, 1000, p), p))));
  const mistura = [];
  const ate = GRADE.filter((p) => p >= 600);
  for (const w of [1, 2, 4, 7, 10, 16, 24]) {
    const pts = ate.map((p) => ponto(orvalhoDaMistura(p, w / 1000), p));
    mistura.push({ w, d: caminho(pts), rotulo: pts[pts.length - 1]! });
  }
  fundoPronto = { isotermas, secas, umidas, mistura };
  return fundoPronto;
}

/** Contorno fechado de uma área de energia: sobe pelo ambiente, desce pela parcela. */
export function contornoDaArea(area: readonly PontoDeArea[]): string {
  const ida = area.map((q) => ponto(q.ambiente, q.p));
  const volta = [...area].reverse().map((q) => ponto(q.parcela, q.p));
  return `${caminho([...ida, ...volta])}Z`;
}

/** Níveis com vento para as barbelas, espaçados de pelo menos `minimo` unidades do `viewBox`. */
export function niveisDoVento(niveis: readonly Nivel[], minimo = 9): Nivel[] {
  const out: Nivel[] = [];
  let ultimo = Infinity;
  for (const n of [...niveis].sort((a, b) => b.p - a.p)) {
    if (n.w == null || n.s == null || n.p > P_BASE || n.p < P_TOPO) continue;
    const y = fracaoDaPressao(n.p) * H;
    if (ultimo - y < minimo) continue;
    out.push(n);
    ultimo = y;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Desenho
// ---------------------------------------------------------------------------

export const COR_T = '#ff6b5e';
export const COR_TD = '#4ade80';
export const COR_PARCELA = '#f4f6fa';
export const COR_CAPE = '#ff5a4f';
export const COR_CIN = '#4ea8de';

const HASTE = 9;

/** Uma barbela: a haste aponta para DE ONDE o vento vem; no hemisfério sul as penas trocam de lado. */
function Barbela(props: { x: number; y: number; direcao: number; nos: number; sul: boolean }) {
  const b = barbela(props.nos);
  if (b.calmo) return <circle cx={props.x} cy={props.y} r="1.6" fill="none" stroke="currentColor" strokeWidth="0.6" />;
  const lado = props.sul ? -1 : 1;
  const marcas = [];
  let y = -HASTE;
  for (let i = 0; i < b.bandeiras; i++) {
    marcas.push(<path key={`b${i}`} d={`M0 ${y}L${3.6 * lado} ${y + 0.9}L0 ${y + 1.8}Z`} fill="currentColor" />);
    y += 2.2;
  }
  for (let i = 0; i < b.tracos; i++) {
    marcas.push(<line key={`t${i}`} x1="0" y1={y} x2={3.6 * lado} y2={y - 1.3} />);
    y += 1.5;
  }
  if (b.meio) {
    // Sozinho, o meio traço não fica na ponta: confundiria com um traço inteiro visto de lado.
    if (!b.bandeiras && !b.tracos) y += 1.5;
    marcas.push(<line key="m" x1="0" y1={y} x2={1.9 * lado} y2={y - 0.7} />);
  }
  return (
    <g transform={`translate(${props.x} ${props.y}) rotate(${props.direcao})`} stroke="currentColor" strokeWidth="0.6" strokeLinecap="round">
      <line x1="0" y1="0" x2="0" y2={-HASTE} />
      {marcas}
    </g>
  );
}

function Marca(props: { rotulo: string; titulo: string; p: number; t: number }) {
  const q = ponto(props.t, props.p);
  return (
    <g>
      <title>{props.titulo}</title>
      <line x1={q.x - 3} y1={q.y} x2={q.x + 3} y2={q.y} stroke={COR_PARCELA} strokeWidth="0.9" />
      <text x={q.x + 4.5} y={q.y + 1.8} fontSize="5" fontWeight="600" fill={COR_PARCELA} stroke="rgba(8,16,24,0.85)" strokeWidth="1.6" paintOrder="stroke">
        {props.rotulo}
      </text>
    </g>
  );
}

const formatoJ = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

export function SkewT(props: {
  niveis: readonly Nivel[];
  /** Latitude da estação: no hemisfério sul as barbelas se espelham. */
  lat: number;
  /** Níveis do perfil inteiro, para o eixo de altura (durante a subida `niveis` é só o que já foi medido). */
  todos?: readonly Nivel[];
  /** Altura do balão no instante do player (m), enquanto ele sobe: a marca do instante. */
  alturaDoBalao?: number | null;
}) {
  const { niveis } = props;
  const todos = props.todos ?? niveis;
  const nuvens = useMemo(() => camadasDeNuvem(niveis), [niveis]);
  const [sob, setSob] = useState<Nivel | null>(null);
  const parcela = useMemo<Parcela | null>(() => parcelaDoPerfil(niveis, P_TOPO), [niveis]);
  const areas = useMemo(() => (parcela ? areasDeEnergia(parcela) : { positivas: [], negativas: [] }), [parcela]);
  const dentro = useMemo(() => [...niveis].filter((n) => n.p <= P_BASE && n.p >= P_TOPO).sort((a, b) => b.p - a.p), [niveis]);
  const ventos = useMemo(() => niveisDoVento(niveis), [niveis]);

  const t = dentro.filter((n) => n.t !== null).map((n) => ponto(n.t!, n.p));
  const d = dentro.filter((n) => n.d !== null).map((n) => ponto(n.d!, n.p));
  // Pressão da marca do instante: a do nível medido mais alto até o balão.
  const pBalao = props.alturaDoBalao != null && dentro.length ? dentro[dentro.length - 1]!.p : null;
  if (t.length < 2)
    return (
      <div className="text-xs text-ink-400">
        {props.alturaDoBalao != null ? 'O balão acabou de subir: ainda sem níveis para desenhar.' : 'Perfil sem níveis suficientes para desenhar.'}
      </div>
    );

  const f = fundo();
  const xVento = PAD_ESQ + W + COL_VENTO / 2 + 1;
  const ler = (clientY: number, alvo: Element) => {
    const r = alvo.getBoundingClientRect();
    // Do pixel da tela para a fração da ÁREA DE DESENHO, não do viewBox.
    const k = (((clientY - r.top) / r.height) * VB_H - PAD_CIMA) / H;
    setSob(nivelMaisProximo(dentro, pressaoDaFracao(k)));
  };

  return (
    <div data-skewt="">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        width="100%"
        role="img"
        aria-label={
          parcela?.nce
            ? `Diagrama Skew-T Log-P: temperatura, ponto de orvalho e parcela levantada, com ${formatoJ.format(parcela.cape)} joules por quilo de energia positiva e ${formatoJ.format(parcela.cin)} de negativa`
            : 'Diagrama Skew-T Log-P: temperatura, ponto de orvalho e parcela levantada, sem área de energia positiva'
        }
        className="block touch-pan-y"
        onMouseLeave={() => setSob(null)}
        onMouseMove={(e) => ler(e.clientY, e.currentTarget)}
        onPointerDown={(e) => e.pointerType !== 'mouse' && ler(e.clientY, e.currentTarget)}
        onPointerMove={(e) => e.pointerType !== 'mouse' && ler(e.clientY, e.currentTarget)}
      >
        <defs>
          <clipPath id="skewt-area">
            <rect x={PAD_ESQ} y={PAD_CIMA} width={W} height={H} />
          </clipPath>
        </defs>

        <g clipPath="url(#skewt-area)" fill="none">
          <g stroke="#e8a15a" strokeWidth="0.35" opacity="0.3">
            {f.secas.map((c, i) => (
              <path key={i} d={c} />
            ))}
          </g>
          <g stroke="#6fb3e8" strokeWidth="0.35" opacity="0.32">
            {f.umidas.map((c, i) => (
              <path key={i} d={c} />
            ))}
          </g>
          <g stroke="#7bd88f" strokeWidth="0.35" strokeDasharray="1.6 1.6" opacity="0.4">
            {f.mistura.map((m) => (
              <path key={m.w} d={m.d} />
            ))}
          </g>
          <g stroke="currentColor">
            {f.isotermas.map((i) => (
              <path key={i.t} d={i.d} strokeWidth={i.t === 0 ? 0.6 : 0.3} opacity={i.t === 0 ? 0.45 : 0.16} />
            ))}
          </g>

          {/* Camadas de nuvem estimadas: faixa clara atrás de tudo. */}
          {nuvens.map((c) => {
            const y0 = PAD_CIMA + fracaoDaPressao(Math.min(P_BASE, c.pBase)) * H;
            const y1 = PAD_CIMA + fracaoDaPressao(Math.max(P_TOPO, c.pTopo)) * H;
            return <rect key={c.pBase} x={PAD_ESQ} y={y1 - 0.8} width={W} height={Math.max(1.6, y0 - y1 + 1.6)} fill="#dfe7f2" fillOpacity="0.13" data-nuvem="" />;
          })}

          {/* As áreas de energia, por baixo das curvas. */}
          {areas.negativas.map((a, i) => (
            <path key={`n${i}`} d={contornoDaArea(a)} fill={COR_CIN} fillOpacity="0.38" data-area="cin" />
          ))}
          {areas.positivas.map((a, i) => (
            <path key={`p${i}`} d={contornoDaArea(a)} fill={COR_CAPE} fillOpacity="0.34" data-area="cape" />
          ))}

          {parcela && (
            <path
              d={caminho(parcela.curva.map((q) => ponto(q.parcela, q.p)))}
              stroke={COR_PARCELA}
              strokeWidth="0.9"
              strokeDasharray="3 2"
              strokeLinejoin="round"
              opacity="0.9"
            />
          )}
          {/* Orvalho primeiro, para a temperatura ficar por cima onde se cruzam. */}
          {d.length > 1 && <path d={caminho(d)} stroke={COR_TD} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />}
          <path d={caminho(t)} stroke={COR_T} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        </g>

        {/* Isóbaras e os dois eixos verticais: hPa à esquerda, km (medidos) à direita. */}
        {ISOBARAS.map((p) => {
          const y = PAD_CIMA + fracaoDaPressao(p) * H;
          const km = kmNoNivel(todos, p);
          return (
            <g key={p}>
              <line x1={PAD_ESQ} y1={y} x2={PAD_ESQ + W} y2={y} stroke="currentColor" strokeWidth="0.35" opacity="0.22" />
              <text x={PAD_ESQ - 2} y={y + 1.9} fontSize="5.2" textAnchor="end" fill="currentColor" opacity="0.6">
                {p}
              </text>
              {km !== null && (
                <text x={VB_W - 0.5} y={y + 1.9} fontSize="5.2" textAnchor="end" fill="currentColor" opacity="0.6">
                  {km < 10 ? km.toFixed(1) : km.toFixed(0)}
                </text>
              )}
            </g>
          );
        })}
        <rect x={PAD_ESQ} y={PAD_CIMA} width={W} height={H} fill="none" stroke="currentColor" strokeWidth="0.4" opacity="0.35" />

        {/* Temperatura na base, e a razão de mistura no fim de cada linha. */}
        {[-30, -20, -10, 0, 10, 20, 30, 40].map((c) => (
          <text key={c} x={ponto(c, P_BASE).x} y={PAD_CIMA + H + 6.5} fontSize="5.2" textAnchor="middle" fill="currentColor" opacity="0.6">
            {c}
          </text>
        ))}
        <g clipPath="url(#skewt-area)">
          {f.mistura.map((m) => (
            <text key={m.w} x={m.rotulo.x} y={m.rotulo.y - 1.2} fontSize="4.4" textAnchor="middle" fill="#7bd88f" opacity="0.75">
              {m.w}
            </text>
          ))}
        </g>

        {parcela && (
          <g clipPath="url(#skewt-area)">
            <Marca rotulo="NCL" titulo="Nível de condensação por levantamento: a base da nuvem" p={parcela.ncl.p} t={parcela.ncl.t} />
            {parcela.nce && Math.abs(parcela.nce.p - parcela.ncl.p) > 25 && (
              <Marca rotulo="NCE" titulo="Nível de convecção espontânea: daqui para cima a parcela sobe sozinha" p={parcela.nce.p} t={parcela.nce.t} />
            )}
            {parcela.ne && <Marca rotulo="NE" titulo="Nível de equilíbrio: o topo da nuvem de tempestade" p={parcela.ne.p} t={parcela.ne.t} />}
          </g>
        )}

        {/* Nuvem também na borda direita do diagrama, sólida: a faixa clara some sobre as áreas de energia. */}
        {nuvens.map((c) => {
          const y0 = PAD_CIMA + fracaoDaPressao(Math.min(P_BASE, c.pBase)) * H;
          const y1 = PAD_CIMA + fracaoDaPressao(Math.max(P_TOPO, c.pTopo)) * H;
          return <rect key={c.pBase} x={PAD_ESQ + W - 3.5} y={y1 - 0.8} width="3" height={Math.max(1.6, y0 - y1 + 1.6)} rx="0.8" fill="#dfe7f2" fillOpacity="0.75" />;
        })}

        {/* Marca do instante: onde o balão está, enquanto sobe. */}
        {pBalao !== null && (
          <g data-marca-balao="">
            <line
              x1={PAD_ESQ}
              y1={PAD_CIMA + fracaoDaPressao(pBalao) * H}
              x2={PAD_ESQ + W}
              y2={PAD_CIMA + fracaoDaPressao(pBalao) * H}
              stroke="rgba(127,227,255,0.85)"
              strokeWidth="0.8"
            />
          </g>
        )}

        {/* Vento: uma haste por nível, na coluna à direita. */}
        {ventos.length > 0 && (
          <g className="text-ink-100" data-barbelas={ventos.length}>
            <line x1={xVento} y1={PAD_CIMA} x2={xVento} y2={PAD_CIMA + H} stroke="currentColor" strokeWidth="0.3" opacity="0.25" />
            {ventos.map((n) => (
              <Barbela key={n.p} x={xVento} y={PAD_CIMA + fracaoDaPressao(n.p) * H} direcao={n.w!} nos={n.s!} sul={props.lat < 0} />
            ))}
          </g>
        )}

        {sob && (
          <g>
            <line
              x1={PAD_ESQ}
              y1={PAD_CIMA + fracaoDaPressao(sob.p) * H}
              x2={PAD_ESQ + W}
              y2={PAD_CIMA + fracaoDaPressao(sob.p) * H}
              stroke="rgba(127,227,255,0.8)"
              strokeWidth="0.6"
            />
            {sob.d !== null && <circle {...centro(sob.d, sob.p)} r="1.9" fill={COR_TD} stroke="rgba(8,16,24,0.8)" strokeWidth="0.5" />}
            {sob.t !== null && <circle {...centro(sob.t, sob.p)} r="1.9" fill={COR_T} stroke="rgba(8,16,24,0.8)" strokeWidth="0.5" />}
          </g>
        )}
      </svg>

      {/* Leitura do nível sob o mouse. Altura fixa para a ficha não pular. */}
      <div className="mt-0.5 flex h-4 items-center gap-2 overflow-hidden text-[10px] whitespace-nowrap tabular-nums">
        {sob ? (
          <>
            <span className="text-white">{(sob.z / 1000).toFixed(1)} km</span>
            <span className="text-ink-400">{sob.p.toFixed(0)} hPa</span>
            {sob.t !== null && <span style={{ color: COR_T }}>{sob.t.toFixed(1)}°</span>}
            {sob.d !== null && <span style={{ color: COR_TD }}>orvalho {sob.d.toFixed(1)}°</span>}
            {sob.w != null && sob.s != null && (
              <span className="text-ink-200">
                vento {Math.round(sob.w)}° · {Math.round(sob.s)} nós
              </span>
            )}
          </>
        ) : (
          <span className="text-ink-400">passe o mouse para ler cada nível</span>
        )}
      </div>

      <div className="text-[10px] text-ink-300 tabular-nums" data-nuvens={nuvens.length}>
        {props.alturaDoBalao != null ? (
          <span>
            Balão subindo: <span className="text-white">{(props.alturaDoBalao / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km</span>
            {nuvens.length > 0 && <> · nuvem até aqui: {nuvens.map(rotuloDaCamada).join(' · ')}</>}
          </span>
        ) : nuvens.length > 0 ? (
          <span>Camadas de nuvem (estimadas pela umidade): {nuvens.map(rotuloDaCamada).join(' · ')}</span>
        ) : (
          <span className="text-ink-400">Sem camada de nuvem pela umidade da sondagem.</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10px] text-ink-400">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-[2px] w-3 rounded" style={{ background: COR_T }} /> temperatura
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-[2px] w-3 rounded" style={{ background: COR_TD }} /> orvalho
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 border-t border-dashed" style={{ borderColor: COR_PARCELA }} /> parcela
        </span>
        <span className="inline-flex items-center gap-1" data-energia="cape">
          <span className="inline-block size-2 rounded-[2px]" style={{ background: COR_CAPE, opacity: 0.6 }} />
          CAPE{parcela?.nce ? ` ${formatoJ.format(parcela.cape)}` : ''}
        </span>
        <span className="inline-flex items-center gap-1" data-energia="cin">
          <span className="inline-block size-2 rounded-[2px]" style={{ background: COR_CIN, opacity: 0.65 }} />
          CIN{parcela?.nce ? ` ${formatoJ.format(parcela.cin)}` : ''}
        </span>
        {nuvens.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block size-2 rounded-[2px] bg-[#dfe7f2]/75" /> nuvem
          </span>
        )}
        <span className="ml-auto">hPa · °C · km</span>
      </div>
    </div>
  );
}

function centro(t: number, p: number): { cx: number; cy: number } {
  const q = ponto(t, p);
  return { cx: q.x, cy: q.y };
}
